import { useCallback, useEffect, useRef } from 'react';
import { useEditorUI } from '../../../../context/EditorUIContext';

/**
 * Hovering a key chip spotlights that key on the canvas for as long as the
 * pointer stays on it, then puts back whatever was spotlighted before — so
 * hovering in the edit sidebar never clobbers a key expanded in the Keys panel.
 *
 * @returns {(keyId: string) => (hovering: boolean) => void}
 */
const useKeyHoverHighlight = () => {
    const { highlightedKeyId, setHighlightedKeyId } = useEditorUI();

    // `undefined` means "not hovering"; `null` is a real saved value.
    const savedRef = useRef(undefined);
    const currentRef = useRef(highlightedKeyId);
    currentRef.current = highlightedKeyId;

    useEffect(
        () => () => {
            if (savedRef.current !== undefined) setHighlightedKeyId(savedRef.current);
        },
        [setHighlightedKeyId]
    );

    return useCallback(
        (keyId) => (hovering) => {
            if (hovering) {
                if (savedRef.current === undefined) savedRef.current = currentRef.current;
                setHighlightedKeyId(keyId);
            } else if (savedRef.current !== undefined) {
                setHighlightedKeyId(savedRef.current);
                savedRef.current = undefined;
            }
        },
        [setHighlightedKeyId]
    );
};

export default useKeyHoverHighlight;
